import { useMutation, useQuery } from '@apollo/client';

import {
    faArrowRotateRight,
    faExternalLink,
    faPencil,
    faPlus,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import {
    CREATE_SAP_BUSINESS_ONE_INTEGRATION,
    DELETE_SAP_BUSINESS_ONE_INTEGRATION,
    GET_SAP_BUSINESS_ONE_INTEGRATIONS,
    TRIGGER_SAP_BUSINESS_ONE_INTEGRATION_MASTERDATA_UPDATE,
} from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_ADD_PATH,
    CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_EDIT_PATH,
    CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH,
} from '../../constants.ts';
import { Controller, useForm, useWatch } from 'react-hook-form';
import Button from '../../../core/components/Button.tsx';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { format } from 'date-fns';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';

interface SapBusinessOneFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);

const FORM_DEFAULT_VALUES = {
    name: '',
    companyDb: '',
    username: '',
    password: '',
    restServiceUrl: '',
    maxRecordsPerData: 0,
    avoidDeliveryDatesInThePast: false,
    orderChannelIds: [],
    clientQuery: '',
    itemQuery: '',
    overwriteDefaultShippingMethod: null,
    bpPropertyForAutomation: '',
    bpNoAutomationNoteText: '',
    calculatePriceOnOrderCreate: false,
    customUnitPriceMasterDataField: '',
    uploadSourceFile: false,
    uploadDocumentTypeField: '',
    uploadDocumentTypeEmail: '',
    uploadDocumentTypeDeliveryNote: '',
    uploadDocumentTypeOrder: '',
};

const SapBusinessOneFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                companyDb: integration.companyDb,
                username: integration.username,
                restServiceUrl: integration.restServiceUrl,
                maxRecordsPerData: integration.maxRecordsPerData,
                avoidDeliveryDatesInThePast: integration.avoidDeliveryDatesInThePast,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                itemQuery: integration.itemQuery,
                clientQuery: integration.clientQuery,
                overwriteDefaultShippingMethod: integration.overwriteDefaultShippingMethod,
                bpPropertyForAutomation: integration.bpPropertyForAutomation,
                bpNoAutomationNoteText: integration.bpNoAutomationNoteText,
                calculatePriceOnOrderCreate: integration.calculatePriceOnOrderCreate,
                customUnitPriceMasterDataField: integration.customUnitPriceMasterDataField,
                uploadSourceFile: integration.uploadSourceFile,
                uploadDocumentTypeField: integration.uploadDocumentTypeField,
                uploadDocumentTypeEmail: integration.uploadDocumentTypeEmail,
                uploadDocumentTypeDeliveryNote: integration.uploadDocumentTypeDeliveryNote,
                uploadDocumentTypeOrder: integration.uploadDocumentTypeOrder,
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH)}>
                            {t('sapbusinessone.form.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="companyDb"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.companyDb')} error={formErrors.companyDb}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="username"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.username')} error={formErrors.username}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.password')}
                            error={formErrors.password}
                            helpText={t('secretUpdateOnly')}
                        >
                            <SecretField
                                {...field}
                                secretName="sapBusinessOnePassword"
                                secretId={integration?.id}
                                defaultIsVisible
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="restServiceUrl"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.restServiceUrl')} error={formErrors.restServiceUrl}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="maxRecordsPerData"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.maxRecordsPerData')}
                            error={formErrors.maxRecordsPerData}
                        >
                            <IntegerField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="avoidDeliveryDatesInThePast"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.avoidDeliveryDatesInThePast')}
                            error={formErrors.avoidDeliveryDatesInThePast}
                        >
                            <BooleanField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="itemQuery"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.itemQuery')} error={formErrors.itemQuery}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="clientQuery"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.clientQuery')} error={formErrors.clientQuery}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="overwriteDefaultShippingMethod"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.overwriteDefaultShippingMethod')}
                            error={formErrors.maxRecordsPerData}
                        >
                            <IntegerField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="bpPropertyForAutomation"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.bpPropertyForAutomation')}
                            error={formErrors.bpPropertyForAutomation}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="bpNoAutomationNoteText"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.bpNoAutomationNoteText')}
                            error={formErrors.bpNoAutomationNoteText}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="calculatePriceOnOrderCreate"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.calculatePriceOnOrderCreate')}
                            error={formErrors.calculatePriceOnOrderCreate}
                        >
                            <BooleanField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="customUnitPriceMasterDataField"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.customUnitPriceMasterDataField')}
                            error={formErrors.customUnitPriceMasterDataField}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="uploadSourceFile"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.uploadSourceFile')}
                            error={formErrors.uploadSourceFile}
                        >
                            <BooleanField {...field} />
                        </FormField>
                    )}
                />
                <Controller
                    name="uploadDocumentTypeField"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.uploadDocumentTypeField')}
                            error={formErrors.uploadDocumentTypeField}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="uploadDocumentTypeEmail"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.uploadDocumentTypeEmail')}
                            error={formErrors.uploadDocumentTypeEmail}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="uploadDocumentTypeDeliveryNote"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.uploadDocumentTypeDeliveryNote')}
                            error={formErrors.uploadDocumentTypeDeliveryNote}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="uploadDocumentTypeOrder"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('sapbusinessone.form.uploadDocumentTypeOrder')}
                            error={formErrors.uploadDocumentTypeOrder}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('sapbusinessone.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export const AddSapBusinessOneFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<SapBusinessOneFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_SAP_BUSINESS_ONE_INTEGRATION, {
        refetchQueries: [GET_SAP_BUSINESS_ONE_INTEGRATIONS],
    });
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    companyDb: formData.companyDb,
                    username: formData.username,
                    password: formData.password,
                    maxRecordsPerData: parseInt(formData.maxRecordsPerData),
                    restServiceUrl: formData.restServiceUrl,
                    avoidDeliveryDatesInThePast: formData.avoidDeliveryDatesInThePast,
                    itemQuery: formData.itemQuery,
                    clientQuery: formData.clientQuery,
                    orderChannels: formData.orderChannelIds,
                    overwriteDefaultShippingMethod: parseInt(formData.overwriteDefaultShippingMethod),
                    bpPropertyForAutomation: formData.bpPropertyForAutomation,
                    bpNoAutomationNoteText: formData.bpNoAutomationNoteText,
                    calculatePriceOnOrderCreate: formData.calculatePriceOnOrderCreate,
                    customUnitPriceMasterDataField: formData.customUnitPriceMasterDataField,
                    uploadSourceFile: formData.uploadSourceFile,
                    uploadDocumentTypeField: formData.uploadDocumentTypeField,
                    uploadDocumentTypeEmail: formData.uploadDocumentTypeEmail,
                    uploadDocumentTypeDeliveryNote: formData.uploadDocumentTypeDeliveryNote,
                    uploadDocumentTypeOrder: formData.uploadDocumentTypeOrder,
                },
            });
            await refetch();
            publishToast({ description: t('sapbusinessone.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sapbusinessone.createError'), status: 'error' });
        }
    };

    return (
        <SapBusinessOneFormModal
            title={t('sapbusinessone.form.createTitle')}
            submitButtonLabel={t('sapbusinessone.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH))}
        />
    );
};

const SapBusinessOneRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_SAP_BUSINESS_ONE_INTEGRATION, {
        refetchQueries: [GET_SAP_BUSINESS_ONE_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('sapbusinessone.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sapbusinessone.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(TRIGGER_SAP_BUSINESS_ONE_INTEGRATION_MASTERDATA_UPDATE);
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            publishToast({ description: t('sapbusinessone.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sapbusinessone.updateMasterDataError'), status: 'error' });
        }
    };

    const channels = integration ? [...integration.orderChannels] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {channels.map((channel) => (
                            <span
                                key={channel.id}
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>
                    {integration.masterDataUpdateActive ? (
                        <span className="flex gap-2 items-center animate-pulse">
                            <span className="text-content-success-strong">
                                <LoadingIcon spin />
                            </span>
                            <span>
                                {`${t('sapbusinessone.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                            </span>
                        </span>
                    ) : integration.masterDataUpdateError ? (
                        <div className="flex flex-col gap-1">
                            <span className="text-content-danger-strong">
                                {t('sapbusinessone.masterDataUpdateError')}
                            </span>
                            <span className="text-content-medium text-sm">{integration.masterDataUpdateError}</span>
                        </div>
                    ) : (
                        <span>
                            {t('sapbusinessone.lastMasterDataUpdate', {
                                updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
                            })}
                        </span>
                    )}
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlIconButton onClick={handleUpdateMasterDataIntegration}>
                            <RefreshIcon />
                        </RowControlIconButton>

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const SapBusinessOneIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_SAP_BUSINESS_ONE_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.sapBusinessOneIntegrations || [];
    return (
        <Layout>
            <SettingsNavigation />
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('sapbusinessone.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('sapbusinessone.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('sapbusinessone.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_ADD_PATH)}>
                                <AddIcon />
                                {t('sapbusinessone.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('sapbusinessone.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('sapbusinessone.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('sapbusinessone.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('sapbusinessone.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <SapBusinessOneRow
                                        key={integration.id}
                                        integration={integration}
                                        refetch={refetch}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={5} className="text-center text-content-medium">
                                        {t('sapbusinessone.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default SapBusinessOneIntegrationPage;
